/**
 * Размывать ли страницу под слоем запуска — или притемнить её плотно.
 *
 * Размытие под слоем — один `backdrop-filter`. Страница под ним неподвижна, но
 * над ним каждый кадр перерисовывается знак ожидания, и браузер каждый кадр
 * пересчитывает размытие заново: кэша у него нет. Замер (tools/
 * launch-overlay-web/cost.mjs, 1920×1080): с отрисовкой на видеокарте, даже
 * программной, — 60 кадров в секунду, без видеокарты вовсе — 13–15. Спросить
 * у браузера, как он рисует, нельзя, поэтому решаем в три приёма:
 *
 * 1. Заведомо слабое устройство (`weakDevice`) — сразу плотный слой.
 * 2. Остальным — размытие и проверка делом (`slowFrames`): первые кадры под
 *    слоем идут медленно — слой становится плотным на ходу, и устройство это
 *    запоминает (`rememberPlain`): в следующий раз размытия не будет с начала.
 * 3. Движки без `backdrop-filter`, «меньше движения» и «меньше прозрачности»
 *    отсекает сам CSS (styles.css, `.launch-scrim`).
 *
 * Плотный слой — не урезанный: то же притемнение, тот же знак и подписи,
 * страница под ним читается тенью.
 */

type DeviceHints = Pick<Navigator, 'userAgent' | 'hardwareConcurrency'> & { deviceMemory?: number };

/** Мало памяти или ядер, либо браузер телевизора или приставки. */
export function weakDevice(nav: DeviceHints): boolean {
  if (typeof nav.deviceMemory === 'number' && nav.deviceMemory <= 2) return true;
  if (typeof nav.hardwareConcurrency === 'number' && nav.hardwareConcurrency > 0 && nav.hardwareConcurrency <= 2) return true;
  return /SmartTV|SMART-TV|Tizen|Web0S|webOS|NetCast|HbbTV|BRAVIA|VIDAA|Android TV|GoogleTV|CrKey|AFT[A-Z]|Roku/i
    .test(nav.userAgent);
}

/** Сколько кадров смотрим и с какой задержки: появление слоя само стоит дороже обычного кадра. */
export const PROBE_FRAMES = 10;
export const PROBE_DELAY_MS = 250;
/** Кадр длиннее — меньше 25 в секунду: знак над размытием уже дёргается. */
const SLOW_FRAME_MS = 40;

/**
 * Идут ли кадры под слоем медленно. По медиане, а не по среднему: один
 * затянувшийся кадр (ответ сервера, сборка мусора) — не приговор устройству.
 */
export function slowFrames(gaps: readonly number[]): boolean {
  if (gaps.length < PROBE_FRAMES) return false;
  const sorted = [...gaps].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)]! > SLOW_FRAME_MS;
}

const STORAGE_KEY = 'lumos.launch.scrim';

/**
 * Что устройство знает о себе: `plain` — размытие не тянет (записано прошлой
 * проверкой), `blur` — тянет наверняка, проверять незачем (так помечают
 * устройство руками и стенды для снимков); `null` — ещё не проверяли.
 */
export function rememberedScrim(): 'plain' | 'blur' | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'plain' || value === 'blur' ? value : null;
  } catch {
    return null;
  }
}

export function rememberPlain(): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, 'plain');
  } catch {
    // Хранилище закрыто (приватный режим) — проверим в следующий раз заново.
  }
}
